import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Crown, Lock, ArrowLeft } from "lucide-react";
import { SubscriptionDialog } from "@/components/subscription/SubscriptionDialog";

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

export const SubscriptionGuard = ({ children }: SubscriptionGuardProps) => {
  const { subscription, loading } = useSubscription();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/welcome");
        return;
      }

      // Check if user is admin
      const { data: profile } = await supabase
        .from('profiles')
        .select('stats')
        .eq('id', user.id)
        .single();
      
      const userRole = (profile?.stats as any)?.role;
      setIsAdmin(userRole === 'admin');
      setCheckingAdmin(false);
    };
    checkAuth();
  }, [navigate]);

  // Show loading while checking subscription and admin status
  if (loading || checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Allow admin users to bypass subscription requirement
  if (isAdmin) {
    return <>{children}</>;
  }

  // Check if user has active trial or subscription
  const hasActiveAccess = subscription?.subscribed || subscription?.trial_active;
  
  // If user doesn't have active access, show subscription required screen
  if (!hasActiveAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-blue-900 flex items-center justify-center p-4">
        <div className="max-w-2xl mx-auto text-center relative">
          {/* Back button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate("/welcome")}
            className="absolute -top-16 left-0 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Welcome
          </Button>
          
          <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-2xl border border-purple-200 dark:border-purple-800">
            <Lock className="h-16 w-16 text-purple-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Membership Required
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              Positivio is an exclusive invite-only community. To filter out bots and ensure only genuine, engaged humans join our positive space, membership is required.
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                🛡️ Human Verification • Premium Quality Community
              </p>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                $2.99/month ensures only real, engaged users who value authentic connections
              </p>
            </div>
            <SubscriptionDialog>
              <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3">
                <Crown className="h-5 w-5 mr-2" />
                Join Positivio Club - $2.99/month
              </Button>
            </SubscriptionDialog>
            
            <div className="mt-6 space-y-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Join our exclusive community of authentic, positive users
              </p>
              <div className="flex justify-center space-x-4 text-xs text-gray-400">
                <Button variant="link" onClick={() => navigate("/welcome")} className="text-xs p-0 h-auto">
                  ← Back to Welcome
                </Button>
                <span>•</span>
                <Button variant="link" onClick={() => navigate("/auth")} className="text-xs p-0 h-auto">
                  Have an invite code?
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If subscribed, render the protected content
  return <>{children}</>;
};